import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Play, PlayCircle, ArrowRight } from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";

const SERVER_URL = BASE_URL.replace(/\/api$/, "");

const getThumbnailUrl = (course) => {
  if (course?.trailer?.assets?.thumbnail) return course.trailer.assets.thumbnail;
  if (course?.thumbnail) return course.thumbnail;
  if (course?.image) return `${SERVER_URL}/${course.image}`;
  return null;
};

const ContinueLearningHero = ({ resumeItem }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  if (!resumeItem || !resumeItem.course) return null;

  const course = resumeItem.course;
  const bg = getThumbnailUrl(course);
  const lessonsCompleted = resumeItem.lessonsCompleted ?? 0;
  const totalLessons = resumeItem.totalLessons ?? 0;
  const progressPercent = resumeItem.progressPercent ?? 0;
  const currentLesson = Math.min(lessonsCompleted + 1, totalLessons);

  const handleResume = () => {
    if (course.slug) navigate(`/course/${course.slug}`);
  };

  return (
    <div className="relative rounded-2xl overflow-hidden mb-10 lg:mb-14 shadow-[0_0_40px_rgba(237,26,77,0.12)]">
      {/* Background image — LCP candidate, prioritize it */}
      {bg && (
        <img
          src={bg}
          alt={course.name}
          width={1600}
          height={600}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
        />
      )}

      {/* Dark gradient overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${
          isRTL ? "from-transparent to-black/95" : "from-black/95 to-transparent"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Content */}
      <div className="relative z-10 p-6 md:p-10 lg:p-14 min-h-[480px] md:min-h-[480px] flex flex-col justify-end max-w-2xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1.5 bg-primary/25 border border-primary/50 px-3 py-1 rounded-full">
            <PlayCircle size={14} className="text-primary" weight="fill" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
              {t("home.continue_learning") || "Continue Learning"}
            </span>
          </div>
        </div>

        <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
          {course.name}
        </h1>

        {totalLessons > 0 && (
          <div className="mb-5 max-w-md">
            <p className="text-sm text-white/85 font-medium mb-2">
              {t("home.lesson_of") || "Lesson"} {currentLesson}{" "}
              {t("home.of") || "of"} {totalLessons}
            </p>
            <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={handleResume}
          className="group inline-flex items-center gap-2 bg-white hover:bg-white/90 text-black font-bold px-6 py-3 rounded-xl transition-all w-fit shadow-lg"
        >
          <Play size={18} weight="fill" />
          <span>{t("home.resume") || "Resume"}</span>
          <ArrowRight
            size={16}
            className={`transition-transform group-hover:translate-x-1 ${
              isRTL ? "rotate-180 group-hover:-translate-x-1" : ""
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default ContinueLearningHero;
